import React, { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { fmtDateTime } from "../lib/dates";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const STAGES = [["new", "New"], ["contacted", "Contacted"], ["vetted", "Vetted"], ["board_approved", "Board approved"], ["on_hold", "On hold"]];
export const STAGE_TONE = { new: "bg-stone-200 text-stone-700", contacted: "bg-sky-100 text-sky-800", vetted: "bg-gold/30 text-crimson", board_approved: "bg-palm/15 text-palm", on_hold: "bg-amber-100 text-amber-900" };

export const StageBadge = ({ stage }) => {
  const label = STAGES.find(([v]) => v === (stage || "new"))?.[1] || "New";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STAGE_TONE[stage] || STAGE_TONE.new}`} data-testid={`vetting-stage-${stage || "new"}`}>{label}</span>;
};

export function VettingTracker({ memberId, canEdit, initial, onChange }) {
  const [v, setV] = useState(initial || { stage: "new", history: [] });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(initial || { stage: "new", history: [] }); }, [initial]);
  const set = async (stage) => {
    setBusy(true);
    try { const { data } = await api.patch(`/admin/vetting-tracker/${memberId}`, { stage, note }); setV(data); setNote(""); onChange?.(data); toast.success(`Marked ${STAGES.find(([s]) => s === stage)[1]}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-gold/20 p-3" data-testid="vetting-tracker">
      <div className="flex items-center justify-between gap-2"><p className="flex items-center gap-1 text-sm font-semibold text-crimson"><ClipboardCheck size={15} /> Vetting progress</p><StageBadge stage={v.stage} /></div>
      {canEdit ? (
        <>
          <div className="mt-2 flex flex-wrap gap-1">
            {STAGES.map(([s, l]) => <Button key={s} size="sm" variant={v.stage === s ? "default" : "outline"} className={v.stage === s ? "bg-crimson" : ""} disabled={busy} onClick={() => set(s)} data-testid={`vetting-set-${s}`}>{busy && v.stage !== s ? <Loader2 size={12} className="animate-spin" /> : l}</Button>)}
          </div>
          <Input className="mt-2" placeholder="Note for the board (e.g. spoke on 6/12, explained dues)" value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} data-testid="vetting-note" />
        </>
      ) : <p className="mt-1 text-xs text-muted-foreground">Only admins, the General Secretary and the Electoral Commissioner can update progress.</p>}
      {v.history?.length > 0 && (
        <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto text-xs text-muted-foreground" data-testid="vetting-history">
          {[...v.history].reverse().map((h) => <li key={h.at}><b className="text-foreground">{STAGES.find(([s]) => s === h.stage)?.[1]}</b> · {h.by} · {fmtDateTime(h.at)}{h.note && ` — ${h.note}`}</li>)}
        </ul>
      )}
    </div>
  );
}
